import React, {useState, useEffect} from "react";
import {addressCommentDetails, deleteAddressStreetZip, editAddressStreetZip, likeService} from "../../services/rating";
import moment from "moment";
import CustomLoader from "../../CustomComponents/CustomLoader";
import {sweetAlertQuestion, sweetAlerts} from "../../utils/sweet-alerts";
import {fDate, fDateTime} from "../../utils/format-time";
import CustomRatingStar from "../../CustomComponents/CustomRatingStar";
import Avatar from "@mui/material/Avatar";
import CustomUserName from "../../CustomComponents/CustomUserName";

const MyComments = () => {
	const [firstTimeLoad, setFirstTimeLoad] = useState(true);
	const [loading, setLoading] = useState(true);
	const [loadingButtons, setLoadingButtons] = useState({});

	const [comments, setComments] = useState([]);
	const [mockComments, setMockComments] = useState([]);
	const [filteredComments, setFilteredComments] = useState([]);

	const [filterStatus, setFilterStatus] = useState("all");
	const [sortBy, setSortBy] = useState("date-desc");

	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);
	const [currentPage, setCurrentPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const [totalLength, setTotalLength] = useState(0);

	const [selectedComment, setSelectedComment] = useState(null);
	const [showDetailModal, setShowDetailModal] = useState(false);
	const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);

	const [showEditModal, setShowEditModal] = useState(false);
	const [editingComment, setEditingComment] = useState(null);
	const [editFormData, setEditFormData] = useState({});
	const [editErrors, setEditErrors] = useState({});
	const [searchTerm, setSearchTerm] = useState("");

	const [moderationError, setModerationError] = useState(null);

	// const indexOfLastComment = currentPage * pageSize;
	// const indexOfFirstComment = indexOfLastComment - pageSize;
	// const currentComments = filteredComments.slice(indexOfFirstComment, indexOfLastComment);
	// const totalPages = Math.ceil(filteredComments.length / pageSize);

	const paginate = (pageNumber) => {
		setPage(pageNumber);
	};

	const getMockComments = async () => {
		const dummyComments = [];

		try {
			if (firstTimeLoad) {
				setLoading(false);
			}

			const response = await addressCommentDetails({
				pagesize: pageSize,
				page: page,
				status: filterStatus,
				sortby: sortBy,
			});

			if (response && response.status === true && Array.isArray(response?.data?.list) && response?.data?.list?.length > 0) {
				const transformed = response?.data?.list?.map((item) => {
					const commentDate = item.comment_date ? moment(item.comment_date) : null;
					const submittedDate = item.createdAt ? moment(item.createdAt) : null;
					const likes = item?.likes || [];
					const trueCount = likes.filter((likeItem) => likeItem?.like == true).length;

					return {
						id: item.street_id,
						commentAddress: item.addresses || "Reply Details",
						commentText: item.description || "N/A",
						comment_type: item.comment_type,
						commentDate: commentDate ? commentDate.format("YYYY-MM-DD") : "",
						status: item.is_historic ? "historic" : "fresh",
						submittedDate: submittedDate ? submittedDate.format("YYYY-MM-DD") : "",
						canEdit: !item.is_historic && !(item?.deleted || false),
						daysAgo: commentDate ? moment().diff(commentDate, "days") : 0,
						flagged: item.admin_flagged || false,
						isDemo: true,
						likeCount: trueCount,
						selfLike: item?.selfLike,
						parentComment: item?.ParentComment,
						activity_log: item?.activity_log,
						street_comments: item?.street_comments,
						likes: item?.likes,
						deleted: item?.deleted || false,
					};
				});

				setTotalLength(response?.data?.totalCount);
				const totalPages = Math.ceil(response?.data?.totalCount / pageSize);
				setTotalPages(totalPages);
				setMockComments(transformed);
			} else {
				// Add dummy data when no API data is available
				setMockComments(dummyComments);
				setTotalLength(0);
				setTotalPages(0);
			}
			setLoading(true);
		} catch (error) {
			setLoading(true);
			setMockComments(dummyComments);
			setTotalLength(0);
			setTotalPages(0);
		}
	};

	useEffect(() => {
		getMockComments();
	}, [pageSize, page]);

	useEffect(() => {
		setComments(mockComments);
		// setFilteredComments(mockComments);
		setLoadingButtons({});
	}, [mockComments]);

	useEffect(() => {
		if (firstTimeLoad) {
			setFirstTimeLoad(false);
		} else {
			setPage(1);
		}
	}, []);

	useEffect(() => {
		if (firstTimeLoad) {
			setFirstTimeLoad(false);
		} else {
			setPage(1);
			getMockComments();
		}
	}, [filterStatus, sortBy]);

	const handleViewDetails = (comment) => {
		setSelectedComment(comment);

		setEditingComment(comment);
		setEditFormData({
			id: comment.id,
			commentAddress: comment.commentAddress,
			commentDate: comment.commentDate,
			commentText: comment.commentText || "",
			canEdit: comment.status === "historic" ? false : true,
		});
		setShowDetailModal(true);
	};

	const openEditModal = (comment) => {
		if (!comment.canEdit) {
			if (comment.deleted) {
				sweetAlerts("error", "This comment cannot be edited because it has been removed by an admin.");
			} else {
				sweetAlerts("error", "This comment cannot be edited because it's older than 90 days or has been flagged.");
			}
			return;
		}
		setEditingComment(comment);
		setSelectedComment(comment);
		setEditFormData({
			id: comment.id,
			commentAddress: comment.commentAddress,
			commentDate: comment.commentDate,
			commentText: comment.commentText || "",
			canEdit: comment.status === "historic" ? false : !comment.deleted,
		});
		setEditErrors({});
		setShowEditModal(true);
	};

	const closeEditModal = () => {
		setShowEditModal(false);
		setEditingComment(null);
		setEditFormData({});
		setSelectedComment({});
		setEditErrors({});
		// Clear loading state for edit button
		if (editingComment) {
			setLoadingButtons((prev) => ({...prev, [`edit-${editingComment.id}`]: false}));
		}
	};

	const handleEditInputChange = (field, value) => {
		setEditFormData((prev) => ({
			...prev,
			[field]: value,
		}));

		// Clear errors when user starts typing
		if (editErrors[field]) {
			setEditErrors((prev) => ({
				...prev,
				[field]: null,
			}));
		}
	};

	const validateEditForm = () => {
		const newErrors = {};

		if (!editFormData.commentAddress.trim()) {
			newErrors.commentAddress = "Comment address is required";
		}

		if (!editFormData.commentDate) {
			newErrors.commentDate = "Comment date is required";
		}

		if (editFormData.overallRating === 0) {
			newErrors.overallRating = "Overall rating is required";
		}

		if (!editFormData.commentText.trim()) {
			newErrors.commentText = "Comment text is required";
		} else if (editFormData.commentText.trim().length < 10) {
			newErrors.commentText = "Comment must be at least 10 characters long";
		}

		setEditErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const getChanges = () => {
		const changes = [];

		if (editingComment.commentAddress !== editFormData.commentAddress) {
			changes.push(`Address: "${editingComment.commentAddress}" → "${editFormData.commentAddress}"`);
		}

		if (editingComment.commentDate !== editFormData.commentDate) {
			changes.push(`Comment Date: ${editingComment.commentDate} → ${editFormData.commentDate}`);
		}

		if (editingComment.commentText !== editFormData.commentText) {
			changes.push(`Comment Text: Updated`);
		}

		return changes;
	};

	const handleEditSubmit = async () => {
		if (!validateEditForm()) {
			return;
		}

		sweetAlertQuestion(`You will lose all reactions when editing a comment.`).then(async (result) => {
			if (result === "Yes") {
				setIsSubmittingEdit(true);
				setLoadingButtons((prev) => ({...prev, [`edit-${editingComment.id}`]: true}));

				try {
					// In real app, this would be an API call like: const response = await editComment(editFormData);

					// Simulate API call
					await new Promise((resolve) => setTimeout(resolve, 1000));

					// Update the comment in the list
					setComments((prev) => prev.map((comment) => (comment.id === editFormData.id ? {...comment, ...editFormData} : comment)));

					const response = await editAddressStreetZip({
						...editFormData,
					});

					if (response && response.status === true) {
						sweetAlerts("success", "Comment updated successfully! Your changes have been saved and tracked.");
						await getMockComments();
						closeEditModal();
					} else {
						if (response.data.reasons) {
							setModerationError({
								message: response.message || "Your comment contains restricted content and cannot be published.",
								reasons: response.data.reasons || [],
								policy_tags: response.data.policy_tags || [],
							});
						} else {
							sweetAlerts("error", response.message || "Failed updating comment. Please try again.");
						}
					}
				} catch (error) {
					sweetAlerts("error", error.message || "Failed to update comment. Please try again.");
				} finally {
					setIsSubmittingEdit(false);
					setLoadingButtons((prev) => ({...prev, [`edit-${editingComment.id}`]: false}));
				}
			} else {
				setLoadingButtons((prev) => ({...prev, [`edit-${editingComment.id}`]: false}));
				return;
			}
		});
	};

	// const calculateJobDateStatus = (jobDate) => {
	// 	const daysDiff = moment().diff(moment(jobDate), "days");
	// 	if (daysDiff <= 90) {
	// 		return {status: "fresh", text: "Fresh", color: "success"};
	// 	} else {
	// 		return {status: "historic", text: "Historic", color: "warning"};
	// 	}
	// };

	const handleEditComment = (commentId) => {
		const comment = comments.find((c) => c.id === commentId);
		if (comment) {
			openEditModal(comment);
		}
	};

	const handleDeleteComment = async (commentId) => {
		sweetAlertQuestion().then(async (result) => {
			if (result === "Yes") {
				setLoadingButtons((prev) => ({...prev, [`delete-${commentId}`]: true}));
				try {
					await new Promise((resolve) => setTimeout(resolve, 500));

					const response = await deleteAddressStreetZip(commentId);
					if (response && response.status === true) {
						if (mockComments.length === 1) {
							setPage(page - 1);
						}
						getMockComments();
						sweetAlerts("success", "Comment deleted successfully");
					} else {
						sweetAlerts("error", "Failed deleting comment. Please try again.");
					}
				} catch (error) {
					sweetAlerts("error", "Failed deleting comment. Please try again.");
				} finally {
					setLoadingButtons((prev) => ({...prev, [`delete-${commentId}`]: false}));
				}
			}
		});
	};

	// Handle like functionality
	const handleLike = async (commentId) => {
		setLoadingButtons((prev) => ({...prev, [`like-${commentId}`]: true}));

		try {
			const response = await likeService({street_id: commentId});

			await getMockComments();

			// Update local interactions state
			// setCommentInteractions((prev) => {
			// 	const current = prev[commentId] || {likes: 0, uplifts: 0, userLiked: false, userUplifted: false};
			// 	const newLikes = current.userLiked ? current.likes - 1 : current.likes + 1;
			// 	return {
			// 		...prev,
			// 		[commentId]: {
			// 			...current,
			// 			likes: newLikes,
			// 			userLiked: !current.userLiked,
			// 		},
			// 	};
			// });
		} catch (error) {
			console.error("Error liking comment:", error);
		}
	};

	// Handle uplift functionality
	const getStatusBadge = (status, flagged) => {
		if (flagged) {
			return <span className="badge badge-danger">Flagged</span>;
		}

		switch (status) {
			case "fresh":
				return <span className="badge badge-success">Fresh</span>;
			case "historic":
				return <span className="badge badge-warning">Historic</span>;
			default:
				return <span className="badge badge-secondary">Unknown</span>;
		}
	};

	const getTypeBadge = (type) => {
		switch (type) {
			case true:
				// return <Chip label="📍 Zipcode" variant="outlined" color="success" />;
				return <span className="badge badge-success">📍 Zipcode</span>;
			default:
				// return <Chip label="🏘️ Street" variant="outlined" color="info" />;
				return <span className="badge badge-warning">🏘️ Street</span>;
		}
	};
	const ObjectString = ({recode_data, label, createdAt}) => {
		let changesData = recode_data;

		if (typeof recode_data === "string") {
			try {
				changesData = JSON.parse(recode_data);
			} catch (err) {
				changesData = {};
			}
		}

		const excludeKeys = [
			"sent_by",
			"user_id",
			"recode_id",
			"notification_id",
			"createdAt",
			"updatedAt",
			"recode_data",
			"old_recode_data",
			"deleted",
			"is_read",
			"password",
			"is_suspended",
			"daily_ratings",
			"is_contractor",
			"is_google_verified",
			"auth_otp",
			"admin_edited",
			"admin_blocked",
			"client_name",
			"is_active",
			"is_historic",
		];

		const labelMap = {
			stars: "Rating",
			deleted: "Deleted",
			total_ratings: "Total Ratings",
			active_ratings: "Active Ratings",
			aggregate_rating: "Aggregate Rating",
			is_historic: "Is Historic",
			issue_summary: "Issue Summary",
			type: "Type",
			table: "Table",
			title: "Title",
		};

		const isDateValue = (value) => {
			if (!value) return false;
			if (typeof value !== "string") return false;
			return /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value) || /\d{4}-\d{2}-\d{2}/.test(value);
		};

		// ✅ Format value based on type and key
		const formatValue = (key, value) => {
			if (value === null || value === undefined) return "N/A";
			if (typeof value === "boolean") return value ? "Yes" : "No";
			if (isDateValue(value)) return fDateTime(value);
			if (key === "stars" || key === "rating") return <CustomRatingStar star={Number(value)} />; // ⭐️ Rating stars
			if (typeof value === "object") return JSON.stringify(value);
			return String(value);
		};

		const renderChangeItem = (key, changeData) => {
			if (excludeKeys.includes(key)) return null;
			const readableLabel = labelMap[key] || key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

			if (changeData && typeof changeData === "object" && changeData.hasOwnProperty("to") && changeData.hasOwnProperty("from")) {
				return (
					<div className="change-item" key={key}>
						<div className="change-label">
							<strong>{readableLabel}:</strong>
						</div>
						<div className="change-values ">
							<div className="change-from ">
								<span className="change-label-text">From:</span>
								<span className="change-value from-value">{formatValue(key, changeData.from)}</span>
							</div>
							<div className="change-to w-50">
								<span className="change-label-text">To:</span>
								<span className="change-value to-value">{formatValue(key, changeData.to)}</span>
							</div>
						</div>
					</div>
				);
			}
		};

		return (
			<div className="changes-container">
				<h6 className="changes-title">
					{label} : {createdAt}
				</h6>
				<div className="changes-list">
					{Object.entries(changesData)
						.map(([key, value]) => renderChangeItem(key, value))
						.filter((item) => item !== null)}
				</div>
			</div>
		);
	};

	const formatDateTime = (dateString) => {
		return new Date(dateString).toLocaleString("en-US", {
			year: "numeric",
			month: "short",
			day: "numeric",
			hour: "2-digit",
			minute: "2-digit",
			hour12: true,
		});
	};

	return (
		<div className="dashboard-page">
			<div className="page-header">
				<h2>My Comments</h2>
				<p>View and manage all your submitted comments ({totalLength} total)</p>
			</div>

			{/* Filters and Search */}
			<div className="ratings-controls">
				<div className="filter-section">
					<select className="filter-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
						<option value="all">All Status</option>
						<option value="fresh">Fresh Comments</option>
						<option value="historic">Historic Comments</option>
						<option value="flagged">Flagged Comments</option>
					</select>

					<select className="filter-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
						<option value="date-desc">Newest First</option>
						<option value="date-asc">Oldest First</option>
						{/* <option value="address">Address A-Z</option> */}
					</select>
				</div>
			</div>

			{loading && (
				<div className="ratings-container">
					{mockComments.length === 0 ? (
						<div className="no-ratings">
							<div className="no-ratings-icon">
								<svg width="64" height="64" viewBox="0 0 24 24" fill="none">
									<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="2" />
								</svg>
							</div>
							<h3>No comments found</h3>
							<p>Try adjusting your search or filter criteria</p>
						</div>
					) : (
						<div className="ratings-grid">
							{mockComments.map((comment) => {
								const isRemoved = comment.deleted;
								const removedStyle = isRemoved ? {opacity: 0.4, filter: "grayscale(0.6)"} : {};

								return (
									<div key={comment.id} className={`rating-card ${comment.flagged ? "flagged" : ""}`} style={removedStyle}>
										{comment.flagged && (
											<div className="m-4 mb-0 text-danger" style={{fontSize: "12px"}}>
												This comment has been flagged by the admin. Please contact the admin for clarification.
											</div>
										)}
										<div className="rating-card-header cursor-pointer" onClick={() => handleViewDetails(comment)}>
											<div className="address-info">
												<h4 className="comment-address">{comment.commentAddress}</h4>
												<div className="rating-info">
													{getStatusBadge(comment.status, comment.flagged)} {getTypeBadge(comment.comment_type)}
												</div>
											</div>

											<div className="card-actions">
												<button className="btn-icon" onClick={() => handleViewDetails(comment)} title="View Details">
													<svg width="16" height="16" viewBox="0 0 24 24" fill="none">
														<path d="M1 12S5 4 12 4S23 12 23 12S19 20 12 20S1 12 1 12Z" stroke="currentColor" strokeWidth="2" />
														<circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
													</svg>
												</button>

												{comment.canEdit && !isRemoved && (
													<>
														<button
															className="btn-icon btn-edit"
															onClick={(e) => {
																e.stopPropagation();
																handleEditComment(comment.id);
															}}
															title="Edit Comment"
															disabled={loadingButtons[`edit-${comment.id}`]}>
															{loadingButtons[`edit-${comment.id}`] ? (
																<span className="spinner-small"></span>
															) : (
																<svg width="16" height="16" viewBox="0 0 24 24" fill="none">
																	<path
																		d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13"
																		stroke="currentColor"
																		strokeWidth="2"
																		strokeLinecap="round"
																		strokeLinejoin="round"
																	/>
																	<path
																		d="M18.5 2.50023C18.8978 2.1024 19.4374 1.87891 20 1.87891C20.5626 1.87891 21.1022 2.1024 21.5 2.50023C21.8978 2.89805 22.1213 3.43762 22.1213 4.00023C22.1213 4.56284 21.8978 5.1024 21.5 5.50023L12 15.0002L8 16.0002L9 12.0002L18.5 2.50023Z"
																		stroke="currentColor"
																		strokeWidth="2"
																		strokeLinecap="round"
																		strokeLinejoin="round"
																	/>
																</svg>
															)}
														</button>

														{/* 
													<button
														className="btn-icon btn-delete"
														onClick={(e) => {
															e.stopPropagation();
															handleDeleteComment(comment.id);
														}}
														title="Delete Comment"
														disabled={loadingButtons[`delete-${comment.id}`]}>
														{loadingButtons[`delete-${comment.id}`] ? (
															<span className="spinner-small"></span>
														) : (
															<svg width="16" height="16" viewBox="0 0 24 24" fill="none">
																<path d="M3 6H5H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
																<path
																	d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z"
																	stroke="currentColor"
																	strokeWidth="2"
																	strokeLinecap="round"
																	strokeLinejoin="round"
																/>
															</svg>
														)}
													</button> */}
													</>
												)}
											</div>
										</div>

										{comment?.parentComment ? (
											<>
												<div className="rating-card-body cursor-pointer" onClick={() => handleViewDetails(comment)}>
													<div className="comment-details">
														<div className="detail-item">
															<span className="detail-label">Address to:</span>
															<span className="detail-value">{comment?.parentComment?.addresses?.substring(0, 120)}</span>
														</div>

														<div className="detail-item">
															<span className="detail-label">Comment Reply to:</span>
															<span className="detail-value">{comment?.parentComment?.description?.substring(0, 120)}</span>
														</div>

														<div className="detail-item">
															<span className="detail-label">Comment Owner:</span>
															<span className="detail-value">
																<CustomUserName user={comment?.parentComment?.user} />
															</span>
														</div>
													</div>

													{comment.commentText && (
														<div className="comment-description">
															<p>{comment.commentText.length > 120 ? `${comment.commentText.substring(0, 120)}...` : comment.commentText}</p>
														</div>
													)}
												</div>
											</>
										) : (
											<>
												<div className="rating-card-body cursor-pointer" onClick={() => handleViewDetails(comment)}>
													<div className="comment-details">
														<div className="detail-item">
															<span className="detail-label">Comment Date:</span>
															<span className="detail-value">{moment(comment.commentDate).format("MMM DD, YYYY")}</span>
															<span className="days-ago">({comment.daysAgo} days ago)</span>
														</div>
													</div>

													{comment.commentText && (
														<div className="comment-description">
															<p>{comment.commentText.length > 120 ? `${comment.commentText.substring(0, 120)}...` : comment.commentText}</p>
														</div>
													)}
												</div>
											</>
										)}

										<div className="rating-card-footer cursor-pointer" onClick={() => handleViewDetails(comment)}>
											<div className="footer-left">
												<span className="submitted-date">Submitted: {moment(comment.submittedDate).format("MMM DD, YYYY")}</span>
											</div>

											<div className="footer-right">
												<div className="interaction-buttons">
													<button
														className={`interaction-btn like-btn disabled ${comment?.selfLike?.like ? "active" : ""}`}
														onClick={(e) => {
															e.stopPropagation();
															// handleLike(comment.id);
														}}
														title="Like this comment"
														disabled={isRemoved || loadingButtons[`like-${comment.id}`]}>
														{loadingButtons[`like-${comment.id}`] ? (
															<span className="spinner-small"></span>
														) : (
															// <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
															// 	<path
															// 		d="M20.84 4.61C20.3292 4.099 19.7228 3.69364 19.0554 3.41708C18.3879 3.14052 17.6725 2.99817 16.95 2.99817C16.2275 2.99817 15.5121 3.14052 14.8446 3.41708C14.1772 3.69364 13.5708 4.099 13.06 4.61L12 5.67L10.94 4.61C9.9083 3.5783 8.50903 2.9987 7.05 2.9987C5.59096 2.9987 4.19169 3.5783 3.16 4.61C2.1283 5.6417 1.5487 7.04097 1.5487 8.5C1.5487 9.95903 2.1283 11.3583 3.16 12.39L12 21.23L20.84 12.39C21.351 11.8792 21.7564 11.2728 22.0329 10.6054C22.3095 9.93789 22.4518 9.22249 22.4518 8.5C22.4518 7.77751 22.3095 7.0621 22.0329 6.39464C21.7564 5.72718 21.351 5.12075 20.84 4.61Z"
															// 		stroke="currentColor"
															// 		strokeWidth="2"
															// 		strokeLinecap="round"
															// 		strokeLinejoin="round"
															// 	/>
															// </svg>
															<div className={comment?.selfLike?.like ? "bi-hand-thumbs-up-fill" : "bi-hand-thumbs-up"} style={{fontSize: 14}} />
														)}
														<span>{loadingButtons[`like-${comment.id}`] ? "Liking..." : "Like"}</span>
														<span className="interaction-count">({comment?.likeCount || 0})</span>
													</button>

													<button className="interaction-btn like-btn disabled">
														<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
															<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
														</svg>
														<span>Replies</span>
														<span className="interaction-count">({comment?.street_comments?.length || 0})</span>
													</button>
												</div>
											</div>
										</div>
									</div>
								);
							})}
						</div>
					)}

					{/* Pagination */}
					{totalPages > 1 && (
						<div className="pagination">
							<button className="pagination-btn" onClick={() => paginate(page - 1)} disabled={page === 1}>
								Previous
							</button>

							<div className="pagination-numbers">
								{[...Array(totalPages)].map((_, index) => {
									const ipage = index + 1;
									if (ipage === 1 || ipage === totalPages || (ipage >= page - 1 && ipage <= page + 1)) {
										return (
											<button key={ipage} className={`pagination-number ${page === ipage ? "active" : ""}`} onClick={() => paginate(ipage)}>
												{ipage}
											</button>
										);
									} else if (ipage === page - 2 || ipage === page + 2) {
										return (
											<span key={ipage} className="pagination-ellipsis">
												...
											</span>
										);
									}
									return null;
								})}
							</div>

							<button className="pagination-btn" onClick={() => paginate(page + 1)} disabled={page === totalPages}>
								Next
							</button>
						</div>
					)}
				</div>
			)}

			{!loading && (
				<div className="ratings-container">
					<CustomLoader />
				</div>
			)}

			{/* Detail Modal */}
			{showDetailModal && selectedComment && (
				<div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
					<div className="modal-content" onClick={(e) => e.stopPropagation()}>
						<div className="modal-header">
							<h3>{selectedComment.parentComment ? "Reply Details" : selectedComment?.comment_type ? "Zipcode Comment Details" : "Street Comment Details"}</h3>
							<button className="modal-close" onClick={() => setShowDetailModal(false)}>
								×
							</button>
						</div>

						<div className="modal-body">
							{selectedComment.parentComment ? (
								<>
									<div className="detail-section">
										{/* <h4>Comment Information</h4> */}

										<h4 className="d-flex justify-content-between">
											Comment Information
											<div className="detail-item">
												<span className="detail-value">
													<span className="detail-label">Date: </span>
													{moment(selectedComment.parentComment.commentDate).format("MMM DD, YYYY")}
													{/* <span className="days-ago"> ({selectedComment.daysAgo} days ago)</span> */}
												</span>
											</div>
										</h4>

										<div className="job-description-full">
											<p>{selectedComment.parentComment?.description}</p>
										</div>
										<div className="detail-item mt-2 justify-content-end">
											<span className="detail-label">Comment Owner:</span>
											<span className="detail-value">
												<CustomUserName user={selectedComment?.parentComment?.user} />
											</span>
										</div>
									</div>
									<div className="detail-section">
										<h4 className="d-flex justify-content-between">
											Your Reply
											<div className="detail-item">
												<span className="detail-label">Reply Date:</span>
												<span className="detail-value">
													{moment(selectedComment.commentDate).format("MMM DD, YYYY")}
													<span className="days-ago"> ({selectedComment.daysAgo} days ago)</span>
												</span>
											</div>
										</h4>
										<div className="job-description-full">
											<p>{selectedComment.commentText}</p>
										</div>
									</div>
								</>
							) : (
								<>
									<div className="detail-section">
										<h4>Comment Information</h4>
										<div className="detail-grid">
											<div className="detail-item">
												<span className="detail-label">Address:</span>
												<span className="detail-value">{selectedComment.commentAddress}</span>
											</div>
											<div className="detail-item">
												<span className="detail-label">Comment Date:</span>
												<span className="detail-value">
													{moment(selectedComment.commentDate).format("MMM DD, YYYY")}
													<span className="days-ago"> ({selectedComment.daysAgo} days ago)</span>
												</span>
											</div>
											<div className="detail-item">
												<span className="detail-label">Overall Rating:</span>
											</div>
										</div>
									</div>
								</>
							)}

							<div className="detail-section">
								<h4>Activity</h4>
								<div className="text-end">
									<div className="">
										{(() => {
											const actionCount = {};
											return selectedComment?.activity_log?.map((log) => {
												const action = log.action;
												actionCount[action] = (actionCount[action] || 0) + 1;

												let label = "";
												if (action === "CREATE") {
													label = "Submit date";
												} else if (action === "UPDATE") {
													label = actionCount[action] === 1 ? "Update date" : "Again update date";
												}

												return (
													<div key={log.log_id} className="text-muted small mb-1">
														{label}: {fDateTime(log.createdAt)}
														{/* {log.changes && <div className="notification-details">{ObjectString({recode_data: log.changes, label, createdAt: fDateTime(log.createdAt)})}</div>} */}
													</div>
												);
											});
										})()}
									</div>
								</div>

								<div className="d-flex  align-items-center mb-2 mt-3 pt-3 border-top">
									<div className="text-muted small fw-semibold">
										Likes ({selectedComment?.likes?.length}) | Comment ({selectedComment?.street_comments?.length})
									</div>
								</div>

								{selectedComment?.likes && selectedComment?.likes?.length > 0 && (
									<div className="">
										<div className="likes-list">
											{selectedComment?.likes.map((like, likeIndex) => (
												<div key={likeIndex} className="d-flex align-items-center py-1 px-2 mb-1 bg-light rounded">
													<div className="me-2">
														<Avatar variant="rounded" sx={{bgcolor: "#22c55e1a", color: "#28a745"}}>
															<div className="bi-hand-thumbs-up" style={{fontSize: 20}} />
														</Avatar>
													</div>
													<div className="flex-grow-1">
														<div className="fw-semibold small">
															<CustomUserName user={like?.user} sx={{fontWeight: 600}} code />
														</div>
														<div className="text-muted" style={{fontSize: "0.65rem"}}>
															{formatDateTime(like?.createdAt)}
														</div>
													</div>
												</div>
											))}
										</div>
									</div>
								)}

								{selectedComment?.street_comments?.map((comments, likeIndex) => (
									<div key={likeIndex} className="d-flex align-items-center py-1 px-2 mb-1 bg-light rounded">
										<div className="me-2">
											<Avatar variant="rounded" sx={{bgcolor: "#0dcaf01a", color: "#0dcaf0"}}>
												<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
													<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
												</svg>
											</Avatar>
										</div>
										<div className="flex-grow-1">
											<div className="fw-semibold small">
												<CustomUserName user={comments?.user} sx={{fontWeight: 600}}  />
											</div>

											<div className="text-muted" style={{fontSize: "0.65rem"}}>
												{formatDateTime(comments?.createdAt)}
											</div>

											{comments?.description}
										</div>
									</div>
								))}
							</div>

							{/* <div className="detail-section">
								<h4>Comment</h4>
								<div className="job-description-full">
									<p>{selectedComment.commentText}</p>
								</div>
							</div> */}
						</div>

						<div className="modal-footer">
							{comments.canEdit && (
								<button
									className="btn btn-success"
									onClick={() => {
										setShowDetailModal(false);
										setShowEditModal(true);
										// setEditingComment(selectedComment);
									}}>
									Edit
								</button>
							)}
							<button className="btn btn-secondary" onClick={() => setShowDetailModal(false)}>
								Close
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Edit Modal */}
			{showEditModal && editingComment && (
				<div className="modal-overlay" onClick={closeEditModal}>
					<div className="modal-content" style={{maxWidth: "600px"}} onClick={(e) => e.stopPropagation()}>
						<div className="modal-header">
							{/* <h3>Edit Comment</h3> */}

							<h3>{selectedComment.parentComment ? "Edit Reply" : selectedComment?.comment_type ? "Edit Zipcode Comment" : "Edit Street Comment"}</h3>

							<button className="modal-close" onClick={closeEditModal}>
								×
							</button>
						</div>

						<div className="modal-body">
							{editFormData.commentAddress !== "Reply Details" && (
								<div className="form-group">
									<label className="form-label">Comment Address</label>
									<input
										type="text"
										className={`form-input ${editErrors.commentAddress ? "error" : ""}`}
										value={editFormData.commentAddress}
										onChange={(e) => handleEditInputChange("commentAddress", e.target.value)}
										readOnly
										disabled
									/>
									{editErrors.commentAddress && <span className="error-text">{editErrors.commentAddress}</span>}
								</div>
							)}

							{editFormData.commentAddress === "Reply Details" && (
								<>
									<div className="detail-section">
										{/* <h4>Comment Information</h4> */}

										<h4 className="d-flex justify-content-between">
											Comment Information
											<div className="detail-item">
												<span className="detail-value">
													<span className="detail-label">Date: </span>
													{moment(selectedComment.parentComment.commentDate).format("MMM DD, YYYY")}
													{/* <span className="days-ago"> ({selectedComment.daysAgo} days ago)</span> */}
												</span>
											</div>
										</h4>
										<div className="job-description-full">
											<p>{selectedComment.parentComment?.description}</p>
										</div>

										<div className="detail-item mt-2 justify-content-end">
											<span className="detail-label">Comment Owner:</span>
											<span className="detail-value">
												<CustomUserName user={selectedComment?.parentComment?.user} />
											</span>
										</div>
									</div>

									<div className="detail-section">
										<h4>Reply Details</h4>
									</div>
								</>
							)}
							{editFormData.commentAddress !== "Reply Details" && (
								<div className="form-group">
									<label className="form-label">Comment Date</label>
									<input
										type="date"
										onClick={(e) => {
											e.target.showPicker && e.target.showPicker();
										}}
										disabled={editFormData.commentAddress === "Reply Details" ? true : false}
										className={`form-input ${editErrors.commentDate ? "error" : ""}`}
										value={editFormData.commentDate}
										onChange={(e) => handleEditInputChange("commentDate", e.target.value)}
										max={new Date().toISOString().split("T")[0]}
									/>
									{editErrors.commentDate && <span className="error-text">{editErrors.commentDate}</span>}
								</div>
							)}

							<div className="form-group">
								{moderationError && (
									<div
										className="moderation-error-alert"
										style={{
											backgroundColor: "#fee",
											border: "1px solid #fcc",
											borderRadius: "4px",
											padding: "12px",
											marginBottom: "10px",
											color: "#c33",
										}}>
										<strong style={{display: "block", marginBottom: "8px"}}>{moderationError.message}</strong>
										{moderationError.reasons && moderationError.reasons.length > 0 && (
											<div style={{fontSize: "14px", marginTop: "8px"}}>
												<strong>Reasons:</strong>
												<ul style={{margin: "4px 0 0 20px", padding: 0}}>
													{moderationError.reasons.map((reason, idx) => (
														<li key={idx}>{reason}</li>
													))}
												</ul>
											</div>
										)}
										{moderationError.policy_tags && moderationError.policy_tags.length > 0 && (
											<div style={{fontSize: "12px", marginTop: "8px", color: "#999"}}>Tags: {moderationError.policy_tags.join(", ")}</div>
										)}
									</div>
								)}

								<label className="form-label">Comment Text</label>
								<textarea
									className={`form-textarea ${editErrors.commentText ? "error" : ""}`}
									value={editFormData.commentText}
									onChange={(e) => handleEditInputChange("commentText", e.target.value)}
									rows="4"
									placeholder="Describe your experience with this job..."
								/>
								{editErrors.commentText && <span className="error-text">{editErrors.commentText}</span>}
							</div>
						</div>

						<div className="modal-footer">
							<button className="btn btn-secondary" onClick={closeEditModal} disabled={isSubmittingEdit}>
								Cancel
							</button>
							<button className="btn btn-primary" onClick={handleEditSubmit} disabled={isSubmittingEdit}>
								{isSubmittingEdit ? (
									<>
										<span className="spinner-small"></span>
										Updating...
									</>
								) : (
									"Update Comment"
								)}
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default MyComments;
